export const en = {
  language: "English",
  nav: {
    home: "Home",
    products: "Products",
    about: "About",
    contact: "Contact",
    news: "News",
    resources: "Resources",
    quickStart: "Documentation",
    documentation: "Documentation",
    apiReference: "API Reference",
    tutorials: "Tutorials",
    learnMore: "Learn More",
    rma: "RMA Application",
    login: "Login",
    search: "Search",
  },
  home: {
    companyIntro: {
      learnMore: "Learn More",
    },
  },
  hero: {
    slide1: {
      title: "Sailflow AI Platform",
      subtitle: "Leading GPU Computing Rental Platform",
      description:
        "Providing flexible and efficient GPU computing resources for AI developers and enterprises to accelerate your AI innovation journey",
      cta: "Get Started",
    },
    slide2: {
      title: "AI Servers & Workstations",
      subtitle: "Professional Computing Solutions",
      description:
        "High-performance AI servers and workstations for deep learning, scientific computing, and more",
      cta: "Learn More",
    },
    slide3: {
      title: "AI Data Center Management Software",
      subtitle: "Intelligent Resource and Application Management",
      description:
        "Easily monitor cluster resources, improve work efficiency, and create your dedicated cloud computing platform",
      cta: "Explore Features",
    },
  },
  about: {
    title: "About Us",
    subtitle:
      "We are Taiwan's leading AI computing service provider, committed to providing the best computing resources and solutions for AI developers and enterprises",
    heading: "Leading AI Computing",
    headingSub: "Innovating the Future",
    desc1:
      "We are a professional AI computing solution provider, dedicated to providing efficient and flexible computing resources for global AI developers and enterprises. Through our innovative Sailflow AI Platform, we make AI computing more accessible and economical.",
    desc2:
      "From AI server hardware to on-premise cluster management software, we provide end-to-end complete solutions to help customers accelerate AI model training, inference deployment, and various high-performance computing tasks. Our mission is to make world-class AI computing power easily accessible to every organization.",
    stats: {
      experience: "Years of Experience",
      clients: "Enterprise Clients",
      nodes: "AI Computing Nodes",
      availability: "Service Availability",
    },
  },
  products: {
    title: "Core Products",
    subtitle: "Comprehensive AI computing solutions for different scenarios",
    learnMore: "Learn More",
    product1: {
      title: "Sailflow AI Platform",
      subtitle: "GPU Cloud Computing Platform",
      description:
        "Flexible pay-as-you-go model, multiple GPU options, one-click AI environment deployment, complete monitoring and management tools.",
      feature1: "Elastic Scaling",
      feature2: "Pay-as-you-go",
      feature3: "Quick Deploy",
      feature4: "Easy Management",
    },
    product2: {
      title: "AI Servers & Workstations",
      subtitle: "Professional Hardware Solutions",
      description:
        "High-performance servers and workstations with latest NVIDIA GPUs, supporting deep learning, scientific computing, rendering and more.",
      feature1: "High Performance",
      feature2: "Customizable",
      feature3: "Enterprise Grade",
      feature4: "Professional Support",
    },
    product3: {
      title: "AI Data Center Management Software",
      subtitle: "Intelligent Resource and Application Management",
      description:
        "Powerful data center management platform with resource scheduling, task management, application management, performance monitoring, and fault warning.",
      feature1: "Smart Scheduling",
      feature2: "Visualization",
      feature3: "Automation",
      feature4: "High Availability",
    },
  },
  news: {
    title: "Latest News",
    sidebarTitle: "News Center",
    subtitle:
      "Stay updated with our latest product releases, technical insights, and industry trends",
    readMore: "Read More",
    viewAll: "View All News",
    category: {
      product: "Product Release",
      tech: "Tech Insights",
      industry: "Industry News",
    },
    article1: {
      title: "Sailflow AI Platform 10.0 Official Release",
      excerpt:
        "The new version brings more powerful features and optimized user experience, supporting more GPU models and flexible billing options...",
      date: "January 15, 2025",
    },
    article2: {
      title: "How to Choose the Right GPU for Deep Learning Training",
      excerpt:
        "In-depth analysis of different GPU models' performance characteristics to help you choose the most suitable computing resources...",
      date: "January 10, 2025",
    },
    article3: {
      title:
        "AI Computing Demand Continues to Grow, GPU Rental Market Prospects",
      excerpt:
        "With the rise of large language models and generative AI, enterprise demand for AI computing is experiencing explosive growth...",
      date: "January 5, 2025",
    },
    tag: {
      hot: "Hot",
      collaboration: "Collab",
      tech: "Tech",
    },
    cyintell: "C.Y.Intell",
    aiInfra: "AI Infra",
    backToHome: "Back to Home",
    contentNotFound: "# Content Not Found\n\nFailed to load the requested content.",
  },
  cases: {
    title: "Client Cases",
    subtitle: "See how we help clients across industries achieve AI innovation",
    viewCase: "View Case",
    challenge: "Challenge",
    solution: "Solution",
    case1: {
      company: "Government Information Department",
      industry: "AI Infrastructure",
      title: "Building Autonomous AI Computing Facilities to Support Cross-Unit Innovative Application Development",
      solutionText:
        "Assisted in building computing, storage, and network management equipment, introduced Sailflow AI Platform to integrate cluster resources, creating an autonomous and controllable AI cloud service system, supporting cross-unit resource scheduling and innovative AI application development.",
      description:
        "Saved 90% of cluster resources and application environment maintenance management time, accelerating the achievement of cross-unit innovation and development goals.",
    },
    case2: {
      company: "Research Institution Information Department",
      industry: "Research Institution",
      title: "Supporting Short- and Medium-Term AI R&D Projects, Reducing Development Cycle and Costs",
      solutionText: "Provided server rental services with built-in Sailflow AI Platform, quickly obtaining powerful computing power and R&D auxiliary systems, accelerating R&D, ensuring information security, and saving the high costs of building and general cloud services.",
      description:
        "Saved over 60% of system setup or cloud service costs, enjoying ample time and resources for complete application R&D and project delivery.",
    },
    case3: {
      company: "University Computer Science Department",
      industry: "Education Research",
      title: "Supporting Course Teaching for 100 People",
      solutionText: "Provided cloud server rental services with built-in Sailflow AI Platform. In the past, only a limited number of GPUs could be built with the same budget. Through cloud GPU services, on-demand access and simultaneous use of multiple GPUs for up to 100 people can be achieved. And through R&D and management auxiliary systems, improve teaching effectiveness and reduce course resource management burden.",
      description:
        "Saved over 70% of system setup or cloud service costs, achieving simultaneous computing use for 100 people and easy management of course resources.",
    },
    case4: {
      company: "Manufacturer AI Software System Service Provider",
      industry: "Manufacturing",
      title: "Manufacturing Defect Detection Model Development Tool Software Based on Multi-Computing Device Clusters",
      solutionText: "Based on Sailflow AI Platform, integrated manufacturing defect detection model development tools, including data annotation, model training, computer vision, data analysis, and more, achieving multi-computing device cluster scheduling for efficient resource utilization and shortened work time.",
      description:
        "Software service providers can focus on key application development, enhancing the integration of software and hardware services, and achieving data center cluster-scale service operations.",
    },
    case5: {
      company: "AI Software System Service Provider in the Healthcare Industry",
      industry: "Healthcare",
      title: "Healthcare AI Model Marketplace",
      solutionText:
        "Based on Sailflow AI Platform's cluster resource management system and application service management system, built a healthcare AI model marketplace, supporting compliant data exchange and Healthcare AI SaaS business model.",
      description:
        "Software service providers can focus on key healthcare application development, enhancing the integration of software and hardware services, and achieving data center cluster-scale service operations.",
    },
    case6: {
      company: "AI Software System Service Provider in the Financial Industry",
      industry: "Financial Services",
      title: "Financial AI Applications",
      solutionText: "Based on Sailflow AI Platform's cluster resource management system and application service management system, built financial AI applications, supporting compliant data exchange and Financial AI SaaS business model.",
      description:
        "Software service providers can focus on key financial application development, enhancing the integration of software and hardware services, and achieving data center cluster-scale service operations.",
    },
  },
  coverage: {
    title: "Global Coverage",
    subtitle: "Our services span major cities and regions worldwide",
    regions: {
      singapore: "Singapore",
      japan: "Japan",
      usa: "USA",
      germany: "Germany",
      uk: "UK",
      thailand: "Thailand",
      taiwan: "Taiwan",
    },
    clients: "Clients",
    DCs: "DCs",
    serviceRegionsClientDistribution: "Service Regions & Client Distribution",
    dataCenters: "Data Centers",
    serviceCountries: "Service Countries",
    enterpriseClients: "Enterprise Clients",
    gpuNodes: "GPU Nodes",
    global: {
      serviceStatistics: "Global Service Statistics",
      service: "Global Service Coverage",
      serviceContent:
        "We have established modern data centers in major cities across the United States, Japan, Thailand, Singapore, Taiwan, United Kingdom, and Germany, equipped with the most advanced AI computing equipment. No matter where your business is located, you can enjoy fast and stable AI computing services.",
      serviceFeature: [
        "Low-latency network connections for optimal performance",
        "99.9% service availability guarantee",
        "24/7 technical support team",
        "Data centers compliant with international security standards",
      ],
    },
  },
  testimonials: {
    title: "Client Testimonials",
    subtitle: "Hear what our clients say about our services",
    testimonial1: {
      content:
        "As a company focused on LLM pre-training and fine-tuning, what we value most is not simply the number of GPUs, but the sustained stability of computing power and network transmission efficiency. Before working with C.Y.Intell, environment configuration and resource scheduling often consumed more than 30% of our R&D team's time. Since adopting C.Y.Intell's AI computing platform, our R&D efficiency has improved significantly. The platform's rapid environment deployment, excellent computing elasticity, and exceptional local service have made a strong impression on our engineers and successfully boosted our development productivity.",
      author: "",
      position: "CEO",
      company: "APMIC",
    },
    testimonial2: {
      content:
        "As a user, I sincerely recommend C.Y.Intell's GPU computing hardware and software. The system is stable and deployment is smooth. When technical issues arise, customer service and engineers respond quickly and handle them thoroughly. Whether for AI training or daily computing, we can confidently rely on them, making our projects more efficient and worry-free.",
      author: "",
      position: "CEO",
      company: "Digital Design Application Technology",
    },
    testimonial3: {
      content:
        "Thanks to C.Y.Intell for saving us a lot of trouble with GPU low-level hardware control. The system is stable and the deployment process is very smooth. There are also dedicated support contacts for related issues. In today's era of team collaboration, our products and projects greatly need this AI Infra support, enabling our image recognition, data analysis, and LLM products to succeed in every client collaboration.",
      author: "",
      position: "Chief Strategy Officer",
      company: "Manufacturing AI Solution Technology",
    },
    testimonial4: {
      content:
        "The locally developed management software, with a Chinese interface, is very intuitive and easy to use. We purchased the software and deployed it on our own AI servers. After using it for a year, it has been very convenient with no major issues, and the cost is reasonable. Highly recommended.",
      author: "",
      position: "Senior Analyst",
      company: "Ministry of Health and Welfare",
    },
  },
  faq: {
    title: "FAQ",
    subtitle: "Answers to your questions",
    moreQuestions: "Have more questions",
    contactUs: "Contact Us",
    q1: {
      question: "What GPU models does Sailflow AI Platform support?",
      answer:
        "We support NVIDIA's full range of GPUs, AMD NAVI4 and above series GPUs, Phison aiDAPTIV+ SSDs, as well as x86 and ARM CPUs.",
    },
    q2: {
      question:
        "How is billing calculated? What payment methods are available?",
      answer:
        "For cloud services, we offer a flexible pay-as-you-go model, charging based on resource usage and duration, supporting hourly billing and monthly packages. We support various payment methods such as credit cards and bank transfers. For on-premise deployment versions, billing is based on the number of GPUs deployed, with an annual subscription model, and a service fee for deployment and installation at your location.",
    },
    q3: {
      question: "What technical support services are provided?",
      answer:
        "We provide technical support during business hours, including system deployment, troubleshooting, etc. Enterprise customers can also enjoy dedicated technical consulting services.",
    },
    q4: {
      question: "Is there a trial available?",
      answer:
        "Yes, we offer free trial credits for new users, allowing you to experience the platform's features and performance before committing to a full subscription.",
    },
  },
  contact: {
    title: "Contact Us",
    subtitle:
      "Have any questions or needs? Feel free to reach out to us anytime",
    phone: "Phone",
    email: "Email",
    address: "Address",
    addressDetail:
      "7F-4, No. 77, Sec. 2, Dunhua S. Rd., Da'an Dist., Taipei City",
    contactInfo: "Contact Information",
    businessHours: "Business Hours",
    businessHoursDetail: "Monday to Friday 9:00-18:00",
    followUs: "Follow Us",
    sendMessage: "Send Message",
    interestedProduct: "Interested Product",
    selectProduct: "Please Select",
    maxCharacters: "Maximum 500 characters",
    messagePlaceholder: "Please describe your needs or questions...",
    namePlaceholder: "Please enter your name",
    companyPlaceholder: "Please enter company name",
    form: {
      name: "Name",
      email: "Email",
      phone: "Phone",
      company: "Company",
      message: "Message",
      submit: "Submit",
    },
  },
  newsletter: {
    title: "Subscribe to Our Newsletter",
    subtitle:
      "Get the latest product information, technical articles, and industry trends",
    placeholder: "Enter your email address",
    button: "Subscribe",
    privacy:
      "We respect your privacy and will not share your information with third parties",
    subscribing: "Subscribing...",
    subscribeSuccess: "Subscription successful! Thank you for your interest.",
    subscribeError: "Subscription failed, please try again later.",
  },
  footer: {
    description:
      "Professional AI computing solution provider, empowering AI innovation",
    products: "Products",
    quickLinks: "Quick Links",
    support: "Support",
    contact: "Contact Us",
    links: {
      platform: "Sailflow AI Platform",
      servers: "AI Servers & Workstations",
      software: "AI Cluster Management Software",
      cases: "Client Cases",
      news: "Latest News",
      contact: "Contact",
      docs: "Documentation",
      faq: "FAQ",
      support: "Technical Support",
      training: "Training",
      terms: "Term of Use",
      privacy: "Privacy Policy",
    },
    copyright: "© 2026 C.Y. Intelligence Co., Ltd. All rights reserved.",
    poweredBy: "Website Builder",
  },

  // Learn More page translations
  learnMore: {
    title: "Learn More",
    sidebarTitle: "Documentation Center",
    subtitle: "Explore in-depth information about our products and services",
    sections: {
      introduction: "About C.Y.Intell",
      platform: "Sailflow AI Platform",
      gpuServers: "AI Servers",
      cluster: "Cluster Management",
      useCases: "Use Cases",
      pricing: "Pricing",
      support: "Support",
      faq: "FAQ",
    },
    backToHome: "Back to Home",
    contentNotFound:
      "# Content not found\n\nThe requested content could not be loaded.",
  },

  // Terms page translations
  terms: {
    title: "Terms",
    sidebarTitle: "Terms & Privacy",
    subtitle: {
      termsOfUse: "Terms of Use",
      privacyPolicy: "Privacy Policy",
      slaService: "SLA Service",
      personalTerm: "Personal Data",
    },
  },

  // RMA page translations
  rma: {
    title: "RMA Application",
    backToHome: "Back to Home",
    application: "RMA Application Form",
    description:
      "Please complete the following information and we will process your RMA application as soon as possible",
    customerName: "Customer Name",
    purchaseDate: "Purchase Date",
    purchaseDateFormat: "Format example: YYYY-MM-DD",
    deliveryAddress: "Delivery Address",
    shippingAddress: "Shipping Address",
    sameAsDelivery: "Same as delivery address",
    mobilePhone: "Mobile Phone",
    modelName: "Model Name",
    serialNumber: "Serial Number",
    repairQuantity: "Repair Quantity",
    defectSymptom: "Defect Symptom",
    defectOptions: [
      "Poor signal",
      "No signal",
      "No power",
      "Port failure",
      "LED malfunction",
      "Unable to connect",
      "Setup failure",
      "Disconnections",
      "System crashes",
      "WAN (network) not detected",
      "Hardware not detected",
      "Power adapter",
      "Reset issues",
      "Physical defect",
      "Other",
    ],
    accessoryOptions: [
      "AI Workstation",
      "AI Server",
      "Baseboard",
      "Power",
      "RAM",
      "CPU",
      "GPU",
    ],
    returnAccessories: "Return Accessories",
    submitApplication: "Submit Application",
    submitting: "Submitting...",
    submitSuccess:
      "Application submitted successfully! We will contact you soon.",
    submitError: "Submission failed, please try again later.",
  },

  // Quick Start page translations
  quickStart: {
    title: "Documentation",
    sidebarTitle: "Documentation Center",
    subtitle: "Get started quickly with our AI computing solutions",
    sections: {
      homepageOverview: "Homepage Overview",
      userAccount: "User Account",
      aiApplications: "AI Applications",
      userFeatures: "User Features",
    },
    backToHome: "Back to Home",
    contentNotFound:
      "# Content not found\n\nThe requested content could not be loaded.",
  },

  // News center translations
  newsCenter: "News Center",
  latestNews: "Latest News",
  companyNews: "Company News",
  pressReleases: "Press Releases",
  events: "Events",
  cookie: {
    title: 'Cookie Privacy Policy',
    description: 'We use cookies to enhance your browsing experience, analyze website traffic, and provide personalized services. You may choose to accept or reject non-essential cookies. For details, please refer to our',
    necessary: 'Necessary Cookies',
    necessaryDesc: 'Required for basic website functionality and cannot be disabled',
    analytics: 'Analytics Cookies',
    analyticsDesc: 'Help us understand how visitors use our website',
    marketing: 'Marketing Cookies',
    marketingDesc: 'Used to deliver personalized ads and marketing content',
    alwaysOn: 'Always On',
    acceptAll: 'Accept All',
    rejectAll: 'Reject All',
    customize: 'Customize',
    savePreferences: 'Save Preferences',
  },

};

export default en;
